import { useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { CurrencyCode } from '@/components/atoms/CurrencyCode';
import { Loader } from '@/components/atoms/Loader';
import { useMessages } from '@/hooks/messages';
import type { EstimationWithDependencies } from '@/hooks/useGetEstimationWithDependencies';
import type { EstimationItemsGroupedTableProps } from '@/pages/estimation/detail/EstimationItemsGroupedTables';
import EstimationItemsGroupedTables from '@/pages/estimation/detail/EstimationItemsGroupedTables';
import type { OmitSafely } from '@/types';

import { mapFromResourceEstimationItemsTableRecord } from '../../mappers/estimationItemsEditFormModelMapper';
import type {
  CostModel,
  EstimationItemsEditFormModel,
  ResourceEstimationItemsTableRecord,
} from '../../model';
import { resourceCostsMessageDefinitions } from './messages';

interface CostingTablesProps {
  data: EstimationWithDependencies;
  dataPending?: boolean;
  estimationCost: CostModel | undefined;
  topLevelEstimationItems: ResourceEstimationItemsTableRecord[];
  tableProps: OmitSafely<
    EstimationItemsGroupedTableProps,
    'estimationItems' | 'control' | 'formModelEstimationItems'
  >;
}

export const CostingTables: FC<CostingTablesProps> = ({
  data,
  estimationCost,
  dataPending,
  topLevelEstimationItems,
  tableProps,
}) => {
  const costsMessages = useMessages(resourceCostsMessageDefinitions);

  const topLevelEstimationItemsFormModel = useMemo<EstimationItemsEditFormModel>(
    () => ({
      estimationItems: topLevelEstimationItems.map((x) =>
        mapFromResourceEstimationItemsTableRecord(
          x,
          data.estimation.methodologyId,
          data.estimationModel,
        ),
      ),
    }),
    [topLevelEstimationItems, data.estimation.methodologyId, data.estimationModel],
  );

  const { control } = useForm<EstimationItemsEditFormModel>({
    defaultValues: topLevelEstimationItemsFormModel,
  });

  return (
    <div>
      {estimationCost && (
        <div className='flex justify-between'>
          {costsMessages.costUnit()}: <CurrencyCode currencyId={estimationCost.currencyId} />
          {dataPending && (
            <div className='flex'>
              <span className='text-s mr-2 ml-2'>{costsMessages.costsRecalculating()}</span>
              <Loader size='s' noMargin />
            </div>
          )}
        </div>
      )}
      <EstimationItemsGroupedTables
        estimationItems={topLevelEstimationItems}
        formModelEstimationItems={topLevelEstimationItemsFormModel.estimationItems}
        control={control}
        {...tableProps}
      />
    </div>
  );
};
